import { type ReactNode, useContext } from 'react';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import Heading from '@alltrails/denali/components/Heading';
import PeakContainerBadge from '@alltrails/denali/components/SubscriptionBadge/Container/Peak';
import { ModalAriaLabelledByContext } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import GenericUpsellTierSubheading from '../UpsellContent/GenericUpsellTierSubheading';
import styles from './ContentBlock.module.scss';

type Props = {
  heading: ReactNode;
  headingAs?: 'h1' | 'h2';
  isPromoEligible?: boolean;
  promoEligibleHeading?: ReactNode;
  promoEligibleSubheading?: ReactNode;
  // Overrides the default price subheading. The plans page passes coupon-aware content computed from server props.
  subheading?: ReactNode;
  tier: PurchaseTier;
};

// Shared "Content Block": heading + subheading (Peak still shows a tier badge). Used by the generic upsell modals and the plans-page redesign.
export default function ContentBlock({
  heading,
  headingAs = 'h2',
  isPromoEligible,
  promoEligibleHeading,
  promoEligibleSubheading,
  subheading,
  tier
}: Props) {
  const displayHeading = promoEligibleHeading && isPromoEligible ? promoEligibleHeading : heading;
  const displaySubheading = promoEligibleSubheading && isPromoEligible ? promoEligibleSubheading : subheading;
  const ariaLabelledById = useContext(ModalAriaLabelledByContext);

  return (
    <div className={styles.container}>
      <div className={styles.lockup}>
        {tier === 'peak' && <PeakContainerBadge size="md" />}
        <Heading as={headingAs} id={ariaLabelledById} size="400">
          {displayHeading}
        </Heading>
      </div>
      {displaySubheading ? (
        <Typography component="div" variant="text200">
          {displaySubheading}
        </Typography>
      ) : (
        <GenericUpsellTierSubheading tier={tier} />
      )}
    </div>
  );
}
